import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { Text } from '@ltvco/refresh-lib/theme';
import { Person } from '@mui/icons-material';

import { BaseCard } from '../BaseCard';
import {
  PersonIconWrapper,
  InfoSection,
  NameTypography,
  SecondaryTextTypography,
  SecondaryTextSubtitleTypography,
  CompaniesList,
} from '../common/components';
import { ProfessionalCardData } from '../common/types';
import { formatName } from '../common/utils';

export function MobileProfessionalCard({
  id,
  name,
  title,
  companies,
  address,
  onCompanyClick,
  onViewReport,
}: ProfessionalCardData) {
  const { trackEventGA4 } = useAppConfigContext();

  const handleOpenProfessionalReport = () => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'primary cta',
      interacted_from: 'Professional Contact',
    });
    onViewReport(id);
  };

  const handleCompanyClick = (index: number) => {
    const companyName =
      typeof displayedCompanies[index] === 'string'
        ? displayedCompanies[index]
        : displayedCompanies[index].name;
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'company cta',
      interacted_from: 'Professional Contact',
    });
    onCompanyClick?.(companyName);
  };

  const formattedName = formatName(name || '');

  let displayedCompanies = companies;

  if (Array.isArray(displayedCompanies)) {
    displayedCompanies = displayedCompanies.filter((company) => company.name);
  }

  // Extract first company name
  const firstCompany =
    displayedCompanies && displayedCompanies.length > 0
      ? typeof displayedCompanies[0] === 'string'
        ? displayedCompanies[0]
        : displayedCompanies[0].name
      : null;

  return (
    <BaseCard
      name={name}
      isExpandable={true}
      onDetailsClick={handleOpenProfessionalReport}
      renderIcon={() => (
        <PersonIconWrapper>
          <Person color="primary" fontSize="small" />
        </PersonIconWrapper>
      )}
      renderHeader={() => (
        <>
          <NameTypography variant="subtitle1" fontWeight="bold">
            {formattedName}
          </NameTypography>
          {title && (
            <SecondaryTextTypography variant="body2">
              {title}
            </SecondaryTextTypography>
          )}
          {firstCompany && (
            <SecondaryTextTypography variant="body2">
              {firstCompany}
            </SecondaryTextTypography>
          )}
          {address && (
            <SecondaryTextTypography variant="body2">
              {address}
            </SecondaryTextTypography>
          )}
        </>
      )}
      renderExpandedContent={() => (
        <InfoSection>
          <SecondaryTextSubtitleTypography variant="subtitle2">
            Companies
          </SecondaryTextSubtitleTypography>
          {companies && companies.length > 0 ? (
            <CompaniesList
              companies={displayedCompanies}
              onClickHandler={handleCompanyClick}
            />
          ) : (
            <Text variant="body2">-</Text>
          )}
        </InfoSection>
      )}
      footerText={formattedName}
      tabName="Professional Contact"
    />
  );
}
